import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, MapPin } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { VenueMapEditor, type EditorZone } from "@/components/venue/VenueMapEditor";
import { parseShape, rectToPolygon, type Point } from "@/lib/venue-shapes";

export const Route = createFileRoute("/dashboard/venues/$venueId")({
  head: () => ({ meta: [{ title: "Editar venue — ZYNTRA" }] }),
  component: VenueEditPage,
});

function VenueEditPage() {
  const { venueId } = Route.useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [venue, setVenue] = useState<{ id: string; name: string; city: string } | null>(null);
  const [mapData, setMapData] = useState<{
    id: string;
    background_image_url: string | null;
    width: number;
    height: number;
  } | null>(null);
  const [zones, setZones] = useState<EditorZone[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/login" });
    if (user) loadData();
  }, [user, loading, venueId]);

  async function loadData() {
    setLoadingData(true);
    const { data: venueData } = await supabase
      .from("venues")
      .select("id, name, city")
      .eq("id", venueId)
      .single();
    if (!venueData) {
      navigate({ to: "/dashboard/venues" });
      return;
    }
    setVenue(venueData);

    const { data: maps } = await supabase
      .from("venue_maps")
      .select("id, background_image_url, width, height")
      .eq("venue_id", venueId)
      .limit(1);
    const map = maps?.[0];
    if (map) {
      setMapData(map);
      const { data: zoneData } = await supabase
        .from("venue_zones")
        .select("id, name, zone_type, color, capacity_total, description, is_active, is_public, sort_order, shape_data")
        .eq("venue_map_id", map.id)
        .order("sort_order");

      setZones(
        (zoneData || []).map((z, i) => {
          const parsed = parseShape(z.shape_data);
          // Fallback layout for zones saved without shape data (legacy form-based)
          const cols = 3;
          const fallbackRect = {
            x: 0.05 + (i % cols) * 0.32,
            y: 0.05 + Math.floor(i / cols) * 0.3,
            w: 0.28,
            h: 0.22,
          };
          let points: Point[];
          if (parsed?.kind === "polygon") {
            points = parsed.points;
          } else if (parsed?.kind === "rect") {
            points = rectToPolygon(parsed.rect);
          } else {
            points = rectToPolygon(fallbackRect);
          }
          return {
            id: z.id,
            name: z.name,
            zoneType: z.zone_type as any,
            color: z.color,
            capacityTotal: z.capacity_total || 0,
            description: z.description || "",
            isActive: z.is_active,
            isPublic: z.is_public,
            points,
          };
        })
      );
    }
    setLoadingData(false);
  }

  if (loading || loadingData) {
    return <div className="mx-auto max-w-6xl px-4 pt-24 pb-20 text-muted-foreground">Cargando...</div>;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 pt-24 pb-20 sm:px-6">
      <Link
        to="/dashboard/venues"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver a venues
      </Link>

      <div className="mb-6">
        <h1 className="text-3xl font-black text-foreground">{venue?.name}</h1>
        <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
          <MapPin className="h-3.5 w-3.5" />
          {venue?.city}
        </p>
      </div>

      {mapData && user && (
        <VenueMapEditor
          mapId={mapData.id}
          initialBackgroundUrl={mapData.background_image_url}
          initialWidth={mapData.width}
          initialHeight={mapData.height}
          initialZones={zones}
          userId={user.id}
          onSaved={loadData}
        />
      )}
    </div>
  );
}
